import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import ChatArea from 'components/ChatArea/ChatArea';
import { themeVariables } from 'theme/globalStyles';

import { useAppConfig } from 'hooks/appConfig';
import { useAuthentication } from 'hooks/authentication';
import { useChatContext } from 'hooks/chatContext';
import { useConversation } from 'hooks/conversation';
import { useCustomer } from 'hooks/customer';
import { useAnalytics } from 'hooks/useAnalytics';
import { usePopularTopics } from 'hooks/usePopularTopics';
import { useView } from 'hooks/view';
import { GlobalProvider } from 'providers/.';

import Routes from 'routes/';
import { getLocalStorageItem, getStorageItem, updateStorageItem } from 'utils/browserCache';
import { ChatInitializeViewType } from 'utils/browserCache/types';
import { getChatContext } from 'utils/chatContext';
import {
  getChatOpenUrlParam,
  getEscalationUrlParam,
  initialChatNavigate,
  initialChatQuestionInput,
  trackChatOpenClose,
  trackChatRouteChanges,
} from './utils';

import { LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY, STORAGE_LOCATION } from 'constants/browserCache';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { ACTIONS } from 'constants/hook';
import { QUERY_STATUS } from 'constants/query';
import { showNameMsgAndEscalate } from 'providers/Global/methods/showNameMsgAndEscalate';
import { ROUTES } from 'routes/constants';

const ChatApp = () => {
  const { trackEvent } = useAnalytics();
  const { dispatchAuthenticationAction, authenticationState } = useAuthentication();
  const { dispatchAppConfigAction, appConfigState } = useAppConfig();
  const { dispatchChatContextAction, chatContextState } = useChatContext();
  const { dispatchConversationAction, conversationState } = useConversation();
  const { dispatchCustomerAction, customerState } = useCustomer();
  const { dispatchViewAction, viewState } = useView();
  const location = useLocation();
  const navigate = useNavigate();

  const isPanelOpen = viewState.isPanelOpen;

  const { data: popularTopicsData, status: popularTopicsFetchStatus } = usePopularTopics(
    chatContextState.mojoContext,
    isPanelOpen,
  );

  const [initializing, setInitializing] = useState(true);

  const storeFunctions = {
    dispatchAuthenticationAction,
    dispatchAppConfigAction,
    dispatchChatContextAction,
    dispatchConversationAction,
    dispatchCustomerAction,
    dispatchViewAction,
    trackEvent,
  };

  const storeStates = {
    authenticationState,
    appConfigState,
    chatContextState,
    conversationState,
    customerState,
    viewState,
  };

  useEffect(() => {
    trackChatRouteChanges(initializing, location);
  }, [location, initializing]);

  useEffect(() => {
    trackChatOpenClose(initializing, viewState);
  }, [viewState, initializing]);

  const [escalationInvoked, setEscalationInvoked] = useState(false);

  useEffect(() => {
    const escalateAsync = async () => {
      if (conversationState.isConnected && getEscalationUrlParam() && !escalationInvoked) {
        {
          await showNameMsgAndEscalate(authenticationState, conversationState);
          setEscalationInvoked(true);
        }
      }
    };
    escalateAsync();
  }, [authenticationState, conversationState.isConnected]);

  /***
   * - identify if existing conversation and execute conditions to trigger initialization
   * - auto open chat based on URL
   */
  useEffect(() => {
    const { authenticationState, customerState } = getChatContext();
    const isExistingConvoInCache = Boolean(
      getLocalStorageItem(LOCAL_STORAGE_KEY.CVNA_PROPEL_CONVERSATION),
    );
    const existingCache = getLocalStorageItem(LOCAL_STORAGE_KEY.CVNA_CHAT_UI);
    const conversationMsgCount = existingCache?.conversationMsgCount ?? 0;

    dispatchAuthenticationAction({
      type: ACTIONS.INITIALIZE,
      payload: authenticationState,
    });

    dispatchCustomerAction({
      type: ACTIONS.INITIALIZE,
      payload: customerState,
    });

    dispatchConversationAction({
      type: ACTIONS.CHANGE,
      payload: { isExistingConversation: isExistingConvoInCache },
    });

    const isOpenChatUrl = getChatOpenUrlParam();

    // Need to initialize a conversation to get a conversationId so we can
    // use a vendor command to escalate the conversation.
    // ref: https://jira.carvana.com/browse/NGCPFE-447
    const isEscalateChat = getEscalationUrlParam();
    if (isEscalateChat && !authenticationState.propel?.conversationId) {
      window[CHATUI_WINDOW_PATH].initializeConversation();
    }
    const currInitializeViewState = getStorageItem(
      STORAGE_LOCATION.SESSION_STORAGE,
      SESSION_STORAGE_KEY.CHAT_INITIALIZE_VIEW,
    ) as ChatInitializeViewType;

    // show to chat history if chat is closed and reopened during existing conversation
    const isExistingConversation =
      isExistingConvoInCache ||
      conversationState.isExistingConversation ||
      Boolean(conversationMsgCount);

    if (!currInitializeViewState?.open && isExistingConversation) {
      navigate(ROUTES.CHAT);
    }

    if (!isPanelOpen && (isOpenChatUrl || currInitializeViewState?.open)) {
      window[CHATUI_WINDOW_PATH].show();

      updateStorageItem(
        STORAGE_LOCATION.SESSION_STORAGE,
        SESSION_STORAGE_KEY.CHAT_INITIALIZE_VIEW,
        {
          ...currInitializeViewState,
          wasInitOpen: currInitializeViewState?.open || false,
        },
      );
      initialChatNavigate(
        currInitializeViewState,
        navigate,
        conversationState,
        dispatchConversationAction,
        isExistingConversation,
      );
      // TODO: We are commenting this out because we think it could be related to the spacebar issue
      initialChatQuestionInput(dispatchConversationAction);
    }

    setInitializing(false);
  }, []);

  /***
   * TECH DEBT / TO IMPROVE OVER TIME:
   * Popular Topics API must be called on all new page loads. Impact of this API is beyond the data it returns.
   * On the backend, it is discreetly providing and impacting AI engine context.
   */
  useEffect(() => {
    if (popularTopicsData || popularTopicsFetchStatus !== QUERY_STATUS.DEFAULT) {
      dispatchChatContextAction({
        type: ACTIONS.CHANGE,
        payload: {
          popularTopics: {
            data: popularTopicsData,
            fetchStatus: popularTopicsFetchStatus,
          },
        },
      });
    }
  }, [popularTopicsData, popularTopicsFetchStatus]);

  const mapFunctionalHooksToClassProps = { location, navigate, storeFunctions, storeStates };

  return (
    <GlobalProvider {...mapFunctionalHooksToClassProps}>
      <ThemeProvider theme={themeVariables}>
        {appConfigState.isEnabled && (
          <>
            {isPanelOpen && <Routes />}
            <ChatArea />
          </>
        )}
      </ThemeProvider>
    </GlobalProvider>
  );
};

export default ChatApp;
